"use client";

import { c as _c } from "react/compiler-runtime";
import { useAppAuth } from "@/hooks/useAppAuth";
import { useOrganizations } from "@/hooks/useOrganizations";
import { clearSupportIdentity, setSupportIdentity } from "@/lib/support";
import { extractEmail } from "@/lib/user/extractEmail";
import { useProfile, useUser } from "@b3dotfun/sdk/global-account/react";
import { useEffect } from "react";

/**
 * Keeps RogerIQ widget identity + org context in sync with the active user.
 * Mirrors PostHogIdentify — mount alongside it so Contact Support prefills
 * the user's email and tags the session with their selected org.
 *
 * Identity is HMAC-signed by /api/support/identity (Secure Mode) so the
 * RogerIQ backend refuses forged identify calls from un-authed browsers.
 */
export function SupportIdentify() {
  const $ = _c(23);
  const {
    user
  } = useUser();
  const {
    isAuthenticated,
    isAuthenticating
  } = useAppAuth();
  const authReady = !isAuthenticating;
  const shouldFetchProfile = authReady && isAuthenticated && !!user?.userId;
  const t0 = shouldFetchProfile ? user?.userId : undefined;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      address: undefined,
      name: undefined,
      b3GlobalId: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: profileData
  } = useProfile(t1);
  const {
    selectedOrgId,
    organizations
  } = useOrganizations();
  let t2;
  if ($[2] !== organizations || $[3] !== selectedOrgId) {
    let t3;
    if ($[5] !== selectedOrgId) {
      t3 = org => org.id === selectedOrgId;
      $[5] = selectedOrgId;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = organizations.find(t3);
    $[2] = organizations;
    $[3] = selectedOrgId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const selectedOrg = t2;
  let t3;
  if ($[7] !== user) {
    t3 = extractEmail(user);
    $[7] = user;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const email = t3;
  const name = profileData?.name ?? profileData?.displayName ?? user?.username ?? undefined;
  let t4;
  if ($[9] !== authReady || $[10] !== email || $[11] !== isAuthenticated || $[12] !== name || $[13] !== selectedOrg?.name || $[14] !== selectedOrgId) {
    t4 = () => {
      if (!authReady) {
        return;
      }
      if (!isAuthenticated || !email) {
        clearSupportIdentity();
        return;
      }
      let cancelled;
      cancelled = false;
      (async () => {
        let signature;
        let signedEmail = email;
        try {
          const res = await fetch(`/api/support/identity`, {
            credentials: "include"
          });
          if (res.ok) {
            const data = (await res.json()) as {
              email?: string;
              signature?: string;
            };
            signature = data.signature;
            if (data.email) {
              signedEmail = data.email;
            }
          }
        } catch {}
        if (cancelled) {
          return;
        }
        setSupportIdentity({
          email: signedEmail,
          name: name ?? undefined,
          orgId: selectedOrgId ?? undefined,
          orgName: selectedOrg?.name,
          signature
        });
      })();
      return () => {
        cancelled = true;
      };
    };
    $[9] = authReady;
    $[10] = email;
    $[11] = isAuthenticated;
    $[12] = name;
    $[13] = selectedOrg?.name;
    $[14] = selectedOrgId;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const t5 = selectedOrg?.name;
  let t6;
  if ($[16] !== authReady || $[17] !== email || $[18] !== isAuthenticated || $[19] !== name || $[20] !== selectedOrgId || $[21] !== t5) {
    t6 = [authReady, isAuthenticated, email, name, selectedOrgId, t5];
    $[16] = authReady;
    $[17] = email;
    $[18] = isAuthenticated;
    $[19] = name;
    $[20] = selectedOrgId;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  useEffect(t4, t6);
  return null;
}